import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import type { Campaign, Account } from '../store/appStore';
import { getTheme } from '../lib/theme';
import { DemokratiukaLayout } from '../components/demokratiuka/DemokratiukaLayout';
import { DemokratiukaFollowGate } from '../components/demokratiuka/DemokratiukaFollowGate';
import { PosterResponseForm } from '../components/demokratiuka/PosterResponseForm';
import { DebateMessageForm } from '../components/demokratiuka/DebateMessageForm';
import { DebateFollowGate } from '../components/demokratiuka/DebateFollowGate';
import { VoteForm } from '../components/demokratiuka/VoteForm';
import { PalmesusLayout } from '../components/palmesus/PalmesusLayout';
import { PalmesusFollowGate } from '../components/palmesus/PalmesusFollowGate';
import { PalmesusSongRequestForm } from '../components/palmesus/PalmesusSongRequestForm';

export const CampaignForm: React.FC = () => {
  const { handle, slug } = useParams<{ handle: string; slug: string }>();
  const location = useLocation();
  const { fetchCampaign, hasFollowed, setHasFollowed } = useAppStore();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const isQR = searchParams.get('source') === 'qr';
  const locationSlug = searchParams.get('loc');

  useEffect(() => {
    setHasFollowed(false);
  }, [setHasFollowed]);

  useEffect(() => {
    if (!handle || !slug) {
      setError('Ugyldig lenke');
      setIsLoading(false);
      return;
    }

    fetchCampaign(handle, slug)
      .then(result => {
        if (!result) {
          setError('Kampanje ikke funnet');
          return;
        }
        setCampaign(result.campaign);
        setAccount(result.account);
      })
      .catch(() => setError('Kunne ikke laste inn informasjon'))
      .finally(() => setIsLoading(false));
  }, [handle, slug]);

  const handleFollowClick = () => {
    if (!account) return;
    const cleanHandle = account.instagramUrl.replace('@', '').trim();
    setHasFollowed(true);
    window.open(`https://instagram.com/${cleanHandle}`, '_blank', 'noopener,noreferrer');
  };

  // For brukere uten Instagram — låser opp formen direkte uten å åpne IG.
  const handleSkipInstagram = () => setHasFollowed(true);

  // Bruk handle-param til å velge layout under loading/error, før account er lastet
  const isPalmesus = handle === 'palmesus' || (account && getTheme(account.theme).style === 'palmesus');

  if (isLoading) {
    if (isPalmesus) {
      return (
        <PalmesusLayout variant="pink">
          <div className="min-h-screen flex items-center justify-center">
            <p style={{ color: '#f4eee7', opacity: 0.8 }}>Laster...</p>
          </div>
        </PalmesusLayout>
      );
    }
    return (
      <DemokratiukaLayout>
        <p style={{ color: '#666' }}>Laster...</p>
      </DemokratiukaLayout>
    );
  }

  if (error || !campaign || !account) {
    if (isPalmesus) {
      return (
        <PalmesusLayout variant="pink">
          <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
            <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 900, fontSize: 'clamp(3rem, 12vw, 6rem)', color: '#f4eee7', letterSpacing: '-0.06em', lineHeight: 0.85 }}>
              Oops.
            </h1>
            <p style={{ color: '#f4eee7', opacity: 0.85, marginTop: '1rem' }}>{error || 'Kampanje ikke funnet'}</p>
          </div>
        </PalmesusLayout>
      );
    }
    return (
      <DemokratiukaLayout>
        <div className="text-center py-16">
          <h1
            className="mb-4"
            style={{
              color: '#000',
              fontSize: 'clamp(1.5rem, 5vw, 2rem)',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            Oops!
          </h1>
          <p style={{ color: '#555' }}>{error || 'Kampanje ikke funnet'}</p>
        </div>
      </DemokratiukaLayout>
    );
  }

  const theme = getTheme(account.theme);

  // Check if campaign is closed
  const isClosed = !!campaign.closesAt;
  if (isClosed) {
    if (theme.style === 'palmesus') {
      return (
        <PalmesusLayout>
          <div
            className="min-h-screen flex flex-col items-center justify-center text-center px-6"
            style={{ backgroundColor: '#e00087', color: '#f4eee7' }}
          >
            <h1 style={{ fontFamily: '"Fraunces", serif', fontWeight: 900, fontSize: 'clamp(3rem, 12vw, 6rem)', letterSpacing: '-0.06em', lineHeight: 0.85, marginBottom: '1rem' }}>
              {campaign.title}
            </h1>
            <p style={{ fontSize: '1.125rem', fontWeight: 500, opacity: 0.92 }}>
              Denne kampanjen er nå avsluttet. Takk for at du var med!
            </p>
          </div>
        </PalmesusLayout>
      );
    }
    return (
      <DemokratiukaLayout>
        <div className="text-center py-16">
          <h1 style={{ color: '#000000', fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 700, lineHeight: 1.2, marginBottom: '0.75rem' }}>
            {campaign.title}
          </h1>
          <p style={{ color: '#666666', fontSize: '1.125rem', fontWeight: 300, lineHeight: 1.7 }}>
            Denne kampanjen er nå avsluttet. Takk for at du deltok!
          </p>
        </div>
      </DemokratiukaLayout>
    );
  }

  // Use Palmesus theme — branded landingside (rosa/burgunder/cream, Fraunces)
  if (theme.style === 'palmesus') {
    return (
      <PalmesusLayout variant={hasFollowed ? 'cream' : 'pink'}>
        {!hasFollowed ? (
          <PalmesusFollowGate account={account} campaign={campaign} onFollow={handleFollowClick} />
        ) : (
          <PalmesusSongRequestForm campaign={campaign} account={account} isQR={isQR} />
        )}
      </PalmesusLayout>
    );
  }

  // Use Demokratiuka theme
  if (theme.style === 'demokratiuka') {
    return (
      <DemokratiukaLayout>
        {!hasFollowed ? (
          campaign.type === 'debate' ? (
            <DebateFollowGate account={account} campaign={campaign} onFollow={handleFollowClick} onSkipInstagram={handleSkipInstagram} />
          ) : (
            <DemokratiukaFollowGate account={account} campaign={campaign} onFollow={handleFollowClick} />
          )
        ) : (
          <>
            {campaign.type === 'poster' && (
              <PosterResponseForm campaign={campaign} account={account} isQR={isQR} location={locationSlug} />
            )}
            {campaign.type === 'debate' && (
              <DebateMessageForm campaign={campaign} account={account} isQR={isQR} />
            )}
            {campaign.type === 'vote' && (
              <VoteForm campaign={campaign} account={account} isQR={isQR} />
            )}
          </>
        )}
      </DemokratiukaLayout>
    );
  }

  // Fallback default dark theme (for non-demokratiuka accounts with campaigns)
  return (
    <div className="min-h-screen bg-[#0a0a0a] p-6">
      <div className="max-w-xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-4">{campaign.title}</h1>
        <p className="text-gray-400">Kampanje ikke konfigurert.</p>
      </div>
    </div>
  );
};
